import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Form, Input, Checkbox, Button, notification } from "antd";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { prepareAgreement, signAgreement, getCompany, editCompany, getCurrentUser, fetchGusData } from "../../../service";
import styled from "styled-components";
import { colors } from "../../../styles/theme";

interface AgreementModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Checkboxes {
  terms: boolean;
  regulations: boolean;
  privacy: boolean;
  marketing_email: boolean;
  marketing_sms: boolean;
  [key: string]: boolean;
}

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.textPrimary};
`;

const ModalDescription = styled.p`
  font-size: 14px;
  color: ${colors.textSecondary};
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const PdfArea = styled.div`
  width: 100%;
  height: 480px;
  background: #e8edf0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  overflow: hidden;
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 24px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;

  .ant-checkbox-wrapper {
    margin-top: 2px;
  }
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  color: ${colors.textPrimary};
  line-height: 1.5;

  a {
    color: ${colors.primary};
    text-decoration: underline;
  }
`;

const Required = styled.span`
  color: var(--color-error);
  margin-right: 2px;
`;

const PrimaryButton = styled(Button)`
  width: 100%;
  font-size: 15px;
  font-weight: 500;
  border-radius: 8px;
`;

export const AgreementModal: React.FC<AgreementModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(0);
  const [agreementId, setAgreementId] = useState<number | null>(null);
  const [pdfPreview, setPdfPreview] = useState<string | null>(null);
  const [smsCode, setSmsCode] = useState("");
  const [checkboxes, setCheckboxes] = useState<Checkboxes>({
    terms: false,
    regulations: false,
    privacy: false,
    marketing_email: false,
    marketing_sms: false,
  });

  const [form] = Form.useForm();
  const [nipFetchLoading, setNipFetchLoading] = useState(false);
  const [nipValidated, setNipValidated] = useState(false);

  const handleFetchGusData = async () => {
    const nip = form.getFieldValue("nip")?.replace(/[\s-]/g, "");
    if (!nip || !/^\d{10}$/.test(nip)) {
      form.setFields([{ name: "nip", errors: [t("agreementModal.nipMustBe10Digits")] }]);
      return;
    }
    setNipFetchLoading(true);
    try {
      const data = await fetchGusData(nip);
      if (!data || !data.name) {
        form.setFields([{ name: "nip", errors: [t("agreementModal.companyNotFoundInGus")] }]);
        setNipValidated(false);
        return;
      }
      form.setFieldsValue({
        companyName: data.name || "",
        streetAddress: data.address || "",
        city: data.city || "",
        regon: data.regon || "",
      });
      form.setFields([{ name: "nip", errors: [] }]);
      setNipValidated(true);
    } catch {
      form.setFields([{ name: "nip", errors: [t("agreementModal.companyNotFoundInGus")] }]);
      setNipValidated(false);
    } finally {
      setNipFetchLoading(false);
    }
  };

  const { data: company } = useQuery(["/admin/company"], () => getCompany(), {
    enabled: open,
  });

  const { data: currentUser } = useQuery(["/admin/current_user"], () => getCurrentUser(), {
    enabled: open,
  });

  // Pre-fill form when modal opens or data arrives
  useEffect(() => {
    if (!open) return;
    if (company || currentUser) {
      form.setFieldsValue({
        firstName: currentUser?.firstName || "",
        lastName: currentUser?.lastName || "",
        companyName: company?.name || "",
        city: company?.city || "",
        streetAddress: company?.adress || "",
        nip: company?.nip || "",
        regon: company?.regon || "",
        phoneNumber: company?.phoneNumber || "",
      });
    }
  }, [open, company, currentUser, form]);

  const { mutateAsync: updateCompanyAsync } = useMutation(editCompany);

  const { mutateAsync: prepareAgreementAsync, isLoading: isPreparingAgreement } =
    useMutation(prepareAgreement, {
      onSuccess: (data) => {
        setAgreementId(data.agreementId);
        setPdfPreview(data.pdfPreview);
        setCurrentStep(1);
      },
      onError: (error: any) => {
        notification.error({
          message: t("agreementModal.error"),
          description: error?.response?.data?.error || t("agreementModal.errorPreparingAgreement"),
        });
      },
    });

  const { mutateAsync: signAgreementAsync, isLoading: isSigningAgreement } =
    useMutation(signAgreement, {
      onSuccess: () => {
        notification.success({
          message: t("agreementModal.onlineSalesEnabled"),
          style: { background: colors.successBackground },
        });
        queryClient.invalidateQueries(["/admin/company"]);
        handleClose();
        onSuccess();
      },
      onError: (error: any) => {
        notification.error({
          message: t("agreementModal.error"),
          description: error?.response?.data?.error || t("agreementModal.errorSigningAgreement"),
        });
      },
    });

  const handleClose = () => {
    setCurrentStep(0);
    setAgreementId(null);
    setPdfPreview(null);
    setCheckboxes({
      terms: false,
      regulations: false,
      privacy: false,
      marketing_email: false,
      marketing_sms: false,
    });
    form.resetFields();
    setNipValidated(false);
    onClose();
  };

  const handleStepOneSubmit = async (values: any) => {
    const { firstName, lastName, companyName, city, streetAddress, nip, regon, phoneNumber } = values;
    const companyAddress = `${streetAddress}, ${city}`;

    // Validate NIP against GUS if not already validated
    if (!nipValidated) {
      setNipFetchLoading(true);
      try {
        const data = await fetchGusData(nip?.replace(/[\s-]/g, ""));
        if (!data || !data.name) {
          form.setFields([{ name: "nip", errors: [t("agreementModal.companyNotFoundInGusCheckNumber")] }]);
          return;
        }
        setNipValidated(true);
      } catch {
        form.setFields([{ name: "nip", errors: [t("agreementModal.companyNotFoundInGusCheckNumber")] }]);
        return;
      } finally {
        setNipFetchLoading(false);
      }
    }

    // Synchronize company data before generating the agreement
    if (company?.id) {
      try {
        await updateCompanyAsync({
          id: company.id,
          data: {
            name: companyName,
            city: city,
            adress: streetAddress,
            nip: nip,
            regon: regon,
            phoneNumber: phoneNumber,
          },
        });
      } catch {
        // Non-blocking — continue even if company update fails (no PIN required for this path)
      }
    }

    await prepareAgreementAsync({
      firstName,
      lastName,
      companyName,
      companyAddress,
      nip,
      regon,
      phoneNumber,
    });
  };

  const handleSignAgreement = async () => {
    if (!agreementId) return;
    if (!checkboxes.terms || !checkboxes.regulations || !checkboxes.privacy) {
      notification.warning({
        message: t("agreementModal.checkAllRequiredConsents"),
      });
      return;
    }
    if (!smsCode || smsCode.length < 6) {
      notification.warning({
        message: t("agreementModal.enterSmsCode"),
      });
      return;
    }
    await signAgreementAsync({ agreementId, checkboxes, smsCode });
  };

  const allRequiredChecked = checkboxes.terms && checkboxes.regulations && checkboxes.privacy;

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={currentStep === 0 ? 540 : 640}
      destroyOnClose
      centered
      title={
        <ModalTitle>{t("agreementModal.modalTitle")}</ModalTitle>
      }
    >
      {currentStep === 0 && (
        <>
          <ModalDescription>
            {t("agreementModal.modalDescription")}
          </ModalDescription>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleStepOneSubmit}
            disabled={isPreparingAgreement || nipFetchLoading}
          >
            <Form.Item
              label="NIP"
              name="nip"
              rules={[
                { required: true, message: t("agreementModal.required") },
                { pattern: /^[\d\s-]{10,13}$/, message: t("agreementModal.nipMustBe10Digits") },
              ]}
            >
              <Input
                placeholder={t("agreementModal.enterNip")}
                maxLength={13}
                onChange={() => setNipValidated(false)}
                suffix={
                  <Button
                    type="link"
                    size="small"
                    loading={nipFetchLoading}
                    onClick={handleFetchGusData}
                    style={{ padding: "0 4px", fontSize: 13 }}
                  >
                    {t("agreementModal.fetchData")}
                  </Button>
                }
              />
            </Form.Item>

            <TwoColumnRow>
              <Form.Item
                label={t("agreementModal.firstName")}
                name="firstName"
                rules={[{ required: true, message: t("agreementModal.required") }]}
              >
                <Input placeholder={t("agreementModal.firstNamePlaceholder")} />
              </Form.Item>
              <Form.Item
                label={t("agreementModal.lastName")}
                name="lastName"
                rules={[{ required: true, message: t("agreementModal.required") }]}
              >
                <Input placeholder={t("agreementModal.lastNamePlaceholder")} />
              </Form.Item>
            </TwoColumnRow>

            <Form.Item
              label={t("agreementModal.companyName")}
              name="companyName"
              rules={[{ required: true, message: t("agreementModal.required") }]}
            >
              <Input placeholder={t("agreementModal.companyNamePlaceholder")} />
            </Form.Item>

            <TwoColumnRow>
              <Form.Item
                label={t("agreementModal.streetAndNumber")}
                name="streetAddress"
                rules={[{ required: true, message: t("agreementModal.required") }]}
              >
                <Input placeholder={t("agreementModal.streetAndNumberPlaceholder")} />
              </Form.Item>
              <Form.Item
                label={t("agreementModal.city")}
                name="city"
                rules={[{ required: true, message: t("agreementModal.required") }]}
              >
                <Input placeholder={t("agreementModal.cityPlaceholder")} />
              </Form.Item>
            </TwoColumnRow>

            <Form.Item
              label="REGON"
              name="regon"
              rules={[
                { required: true, message: t("agreementModal.required") },
                { pattern: /^[\d]{9}$|^[\d]{14}$/, message: t("agreementModal.regonMustBe9Or14Digits") },
              ]}
            >
              <Input placeholder={t("agreementModal.enterRegon")} maxLength={14} />
            </Form.Item>

            <Form.Item
              label={t("agreementModal.contactPhone")}
              name="phoneNumber"
              extra={t("agreementModal.contactPhoneExtra")}
              rules={[
                { required: true, message: t("agreementModal.required") },
                { pattern: /^[\d\s+()-]{9,15}$/, message: t("agreementModal.invalidNumber") },
              ]}
            >
              <Input placeholder="+48 XXX XXX XXX" />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <PrimaryButton
                type="primary"
                htmlType="submit"
                loading={isPreparingAgreement || nipFetchLoading}
              >
                {t("agreementModal.generateDocuments")}
              </PrimaryButton>
            </Form.Item>
          </Form>
        </>
      )}

      {currentStep === 1 && (
        <>
          <PdfArea>
            {pdfPreview ? (
              <iframe
                src={pdfPreview}
                width="100%"
                height="100%"
                title={t("agreementModal.agreementPreview")}
                style={{ border: "none" }}
              />
            ) : (
              <span style={{ color: colors.textSecondary, fontSize: 14 }}>
                {t("agreementModal.pdfPreviewArea")}
              </span>
            )}
          </PdfArea>

          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: colors.textPrimary,
                marginBottom: 8,
              }}
            >
              {t("agreementModal.smsConfirmationCode")}
            </div>
            <Input
              placeholder="000000"
              size="large"
              value={smsCode}
              onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, "").substring(0, 6))}
              style={{
                letterSpacing: "4px",
                textAlign: "center",
                fontSize: "20px",
                fontWeight: "bold",
              }}
            />
            <div
              style={{
                fontSize: 12,
                color: colors.textSecondary,
                marginTop: 4,
              }}
            >
              {t("agreementModal.enter6DigitCode")}
            </div>
          </div>

          <CheckboxList>
            <CheckboxRow>
              <Checkbox
                checked={checkboxes.terms}
                onChange={(e) => setCheckboxes({ ...checkboxes, terms: e.target.checked })}
              />
              <CheckboxLabel>
                <Required>*</Required>
                {t("agreementModal.consentTerms")}
              </CheckboxLabel>
            </CheckboxRow>

            <CheckboxRow>
              <Checkbox
                checked={checkboxes.regulations}
                onChange={(e) => setCheckboxes({ ...checkboxes, regulations: e.target.checked })}
              />
              <CheckboxLabel>
                <Required>*</Required>
                {t("agreementModal.consentRegulationsPrefix")}{" "}
                <a href="https://proste.menu/regulamin" target="_blank" rel="noopener noreferrer">
                  {t("agreementModal.consentRegulationsLink")}
                </a>
                .
              </CheckboxLabel>
            </CheckboxRow>

            <CheckboxRow>
              <Checkbox
                checked={checkboxes.privacy}
                onChange={(e) => setCheckboxes({ ...checkboxes, privacy: e.target.checked })}
              />
              <CheckboxLabel>
                <Required>*</Required>
                {t("agreementModal.consentPrivacyPrefix")}{" "}
                <a href="https://proste.menu/polityka-prywatnosci" target="_blank" rel="noopener noreferrer">
                  {t("agreementModal.consentPrivacyLink")}
                </a>
                .
              </CheckboxLabel>
            </CheckboxRow>

            <CheckboxRow>
              <Checkbox
                checked={checkboxes.marketing_email}
                onChange={(e) => setCheckboxes({ ...checkboxes, marketing_email: e.target.checked })}
              />
              <CheckboxLabel>
                {t("agreementModal.consentMarketingEmail")}
              </CheckboxLabel>
            </CheckboxRow>

            <CheckboxRow>
              <Checkbox
                checked={checkboxes.marketing_sms}
                onChange={(e) => setCheckboxes({ ...checkboxes, marketing_sms: e.target.checked })}
              />
              <CheckboxLabel>
                {t("agreementModal.consentMarketingSms")}
              </CheckboxLabel>
            </CheckboxRow>
          </CheckboxList>

          <PrimaryButton
            type="primary"
            onClick={handleSignAgreement}
            disabled={!allRequiredChecked || !smsCode || smsCode.length < 6}
            loading={isSigningAgreement}
          >
            {t("agreementModal.startOnlineSales")}
          </PrimaryButton>
        </>
      )}
    </Modal>
  );
};
